import { lazy } from 'react';
import * as z from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import {
	createRoute,
	isRedirect,
	redirect,
	type AnyRoute,
} from '@tanstack/react-router';
import { articleOptions } from '@shared/api';
import { ContentLangParam } from '@/constants';
import config from '@/config';

/**
 * The slice of `app/routing/router-context.ts`'s `RouterContext` this loader
 * actually reads -- structural, not imported, since `pages` can't depend on
 * `app` (FSD's dependency direction is one-way: app -> pages, never back).
 * `getRoute`'s `parentRoute: AnyRoute` param already erases the real
 * context generic at this function boundary regardless, so there'd be
 * nothing to import correctly even without the layering rule.
 */
interface LoaderContext {
	queryClient: QueryClient;
	auth: { isAdmin: () => boolean };
}

const articleSearchSchema = z.object({
	lang: ContentLangParam,
});

export const path = 'article/$id';

// Lazy, like every template's other custom routes: the article page brings
// the whole article renderer (PostCSS, Swiper, the reader menu), and a
// static import put all of it into the first load of `daily`'s and
// `toyota`'s home page, which only registers this route.
const LazyArticleView = lazy(() =>
	import('./ui/article').then((m) => ({ default: m.ArticleView })),
);

export function getRoute({ parentRoute, beforeLoad }: RouteParams) {
	return createRoute({
		getParentRoute: () => parentRoute,
		validateSearch: articleSearchSchema,
		beforeLoad,
		path,
		// Topic 08's loader item (issues -> issue -> article -> home):
		// `articleOptions` is the same factory `useGetArticle` calls, so this
		// just starts the fetch as soon as the route matches instead of
		// waiting for `ArticleView` to mount and call the hook itself --
		// removes the route-chunk -> render -> query -> render waterfall.
		// Passes no `isOnline`: a loader has no trustworthy answer to give
		// (see `CacheArgs.isOnline` in `shared/api/queries.ts`), and it
		// doesn't need one. What this route actually wants is "if the article
		// can't be loaded, bounce home rather than dead-end on an error
		// page", and that is a question about the *outcome*, not about
		// connectivity -- so it's answered by catching the failure below
		// instead of predicting it beforehand. That also covers failures the
		// old offline check never did (a request that fails while nominally
		// online, say).
		loader: ({ context, params }: { context: LoaderContext; params: { id: string } }) =>
			context.queryClient
				.ensureQueryData(
					articleOptions(params.id, {
						isAdmin: context.auth.isAdmin(),
						ttlMinutes: config.app?.cache?.ttl?.getIssue,
						// `useNavigate()` isn't available in a loader; `redirect()`
						// is TanStack's own idiom for exactly this (verified it
						// propagates correctly through `ensureQueryData`, not
						// assumed) -- `useGetArticle`'s own `back()` (used when
						// this same factory is called from the component instead)
						// prefers real router history when there's any to go back
						// to, but a loader firing means we're not on this route
						// yet, so there's no "back" to prefer over the tenant's
						// base URL. Still reached when a *hook* passes an
						// explicit `isOnline: false`.
						onUnavailableOffline: () => {
							throw redirect({
								to: config.build?.subdirectory || '/',
								replace: true,
							});
						},
					}),
				)
				.catch((error: unknown) => {
					// A `redirect()` is control flow, not a failure -- let it
					// through untouched (`onUnavailableOffline` above throws
					// one, and so can the auth branches in `fetchArticle`).
					if (isRedirect(error)) {
						throw error;
					}
					// Anything else means we have no article to render, so
					// send the reader home instead of to the route error
					// boundary -- the behaviour this route has always had for
					// "offline with nothing cached", now reached by outcome.
					throw redirect({
						to: config.build?.subdirectory || '/',
						replace: true,
					});
				}),
		component: LazyArticleView,
	});
}

interface RouteParams {
	parentRoute: AnyRoute;
	/**
	 * The template's guard, already resolved against the tenant's auth config
	 * -- the route catalog passes it (`app/routing/route-catalog.tsx`), since
	 * protection isn't part of a route's identity. None leaves it open.
	 */
	beforeLoad?: (opts: {
		params: Record<string, string>;
		search: Record<string, unknown>;
		location: { pathname: string };
	}) => Promise<Record<string, unknown> | undefined>;
}
